import Link from "next/link";
import {
	Band,
	ClosingCta,
	FeatureRow,
	Hero,
	HomeFaq,
	Layout,
	PlatformGrid,
	PlusHighlights,
	ProofStrip
} from "components";
import PromptChips from "components/PromptChips/PromptChips";
import { colors } from "const/colors";
import { assistantPrompts } from "const/content";
import { Article, Section, SectionLarge, Text } from "styles";

export default function Home() {
	return (
		<Layout>
			<Band pad="32px 0 64px">
				<Hero />
				<ProofStrip />
			</Band>

			<Band alt>
				<Section>
					<FeatureRow phone imageUrl="/assets/screens/wishbox.png" mediaAlt="Wishboxes and wallets">
						<Article>
							<Text midExtreme>
								A <Text color={colors.magenta}>$300</Text> surprise should not{" "}
								<Text color={colors.orange}>wreck</Text> your <Text color={colors.blue}>month</Text>
								.
							</Text>
						</Article>
						<Article>
							<Text lede>
								If tires, rent, or a trip will hit later, make a{" "}
								<Text color={colors.blue}>wishbox</Text> with a due date. Wishbox suggests the
								monthly deposit so the money is already labeled when you need it — separate from the
								wallet, bank, or drawer it lives in.
							</Text>
						</Article>
					</FeatureRow>
				</Section>
			</Band>

			<Band>
				<Section>
					<FeatureRow
						invert
						phone
						imageUrl="/assets/screens/logging.png"
						mediaAlt="Logging a new expense"
					>
						<Article>
							<Text midExtreme>
								<Text color={colors.magenta}>Intuitive</Text> and{" "}
								<Text color={colors.teal}>familiar</Text> logging.
							</Text>
						</Article>
						<Article>
							<Text lede>
								Add <Text color={colors.green}>income</Text> or an{" "}
								<Text color={colors.magenta}>expense</Text> in seconds, with categories,
								subcategories, search, and transfers between items. Spot the{" "}
								<Text color={colors.orange}>purchases</Text> you did not mean to do.
							</Text>
						</Article>
					</FeatureRow>
				</Section>
			</Band>

			<Band alt>
				<Section>
					<FeatureRow
						phone
						imageUrl="/assets/screens/month.png"
						mediaAlt="October spending insights"
					>
						<Article>
							<Text midExtreme>
								<Text color={colors.teal}>See</Text> the month{" "}
								<Text color={colors.blue}>clearly</Text>.
							</Text>
						</Article>
						<Article>
							<Text lede>
								Spending pies, month overview, and net worth versus last month — so you know what
								moved, not just what you spent.
							</Text>
						</Article>
					</FeatureRow>
				</Section>
			</Band>

			<Band>
				<Section>
					<FeatureRow
						invert
						phone
						imageUrl="/assets/screens/budgets.png"
						mediaAlt="Monthly budget plan with category limits"
					>
						<Article>
							<Text midExtreme>
								<Text color={colors.teal}>Budgets</Text> for the{" "}
								<Text color={colors.orange}>month</Text> and each{" "}
								<Text color={colors.blue}>category</Text>.
							</Text>
						</Article>
						<Article>
							<Text lede>
								Set a spend limit, then see what is left, what went over, and how many days are
								still in the cycle. Part of <Text color={colors.purple}>Wishbox+</Text>.
							</Text>
						</Article>
					</FeatureRow>
				</Section>
			</Band>

			<Band alt>
				<Section>
					<FeatureRow
						phone
						imageUrl="/assets/screens/brick-puzzle.png"
						mediaAlt="Brick Puzzle for a closed month of spending"
					>
						<Article>
							<Text midExtreme>
								<Text color={colors.orange}>Brick Puzzle</Text>. A month you can{" "}
								<Text color={colors.magenta}>beat</Text>.
							</Text>
						</Article>
						<Article>
							<Text lede>
								A closed month of spending, or a budget, becomes pieces you fit on the board. Race
								your best time. Part of <Text color={colors.purple}>Wishbox+</Text>.
							</Text>
						</Article>
					</FeatureRow>
				</Section>
			</Band>

			<Band>
				<Section>
					<FeatureRow
						invert
						mediaSrc="/assets/promo/plus_assistant.png"
						mediaAlt="Wishbox assistant"
						dark
					>
						<Article>
							<Text midExtreme>
								<Text color={colors.blue}>Ask</Text> Wishbox to{" "}
								<Text color={colors.purple}>do the work</Text>.
							</Text>
						</Article>
						<Article>
							<Text lede>
								<Text color={colors.green}>Chat</Text> or <Text color={colors.blue}>speak</Text> to
								add <Text color={colors.magenta}>transactions</Text>, import a bank export, catch
								duplicates, and understand your finances. Voice works on your phone and on{" "}
								<Text color={colors.purple}>watch</Text>.
							</Text>
						</Article>
					</FeatureRow>
				</Section>
				<Section>
					<SectionLarge style={{ width: "100%", minWidth: 0 }}>
						<Article>
							<PromptChips prompts={assistantPrompts} />
						</Article>
					</SectionLarge>
				</Section>
				<Section>
					<FeatureRow
						mediaSrc="/assets/promo/plus_hero.png"
						mediaAlt="Assistant and collaboration on two phones"
						dark
					>
						<Article>
							<Text lede>
								The assistant can create a wishbox, deposit what is left this month, or walk through
								a file you already exported.{" "}
								<Text color={colors.magenta}>Nothing leaves the device until you agree</Text>.
							</Text>
						</Article>
					</FeatureRow>
				</Section>
			</Band>

			<Band alt>
				<Section>
					<FeatureRow
						invert
						mediaSrc="/assets/promo/plus_collaboration.png"
						mediaAlt="Shared wishbox items"
						dark
					>
						<Article>
							<Text midExtreme>
								<Text color={colors.orange}>Share</Text> an item, not your{" "}
								<Text color={colors.purple}>whole life</Text>.
							</Text>
						</Article>
						<Article>
							<Text lede>
								Invite someone to a wallet, a house fund, or a trip. Only that item’s{" "}
								<Text color={colors.magenta}>transactions</Text> stay in sync. Everything else{" "}
								<Text color={colors.blue}>stays on your device</Text>.
							</Text>
						</Article>
					</FeatureRow>
				</Section>
			</Band>

			<Band>
				<Section>
					<FeatureRow
						mediaSrc="/assets/promo/plus_widgets_info.png"
						mediaAlt="Total net worth widget"
						dark
					>
						<Article>
							<Text midExtreme>
								<Text color={colors.blue}>Widgets</Text> on the{" "}
								<Text color={colors.teal}>home screen</Text> and in the app.
							</Text>
						</Article>
						<Article>
							<Text lede>
								Net worth, month balance, category spend, streaks, deposits, keep-up, and a new
								transaction shortcut — plus a customizable grid inside Wishbox.
							</Text>
						</Article>
					</FeatureRow>
				</Section>
				<Section>
					<FeatureRow
						invert
						mediaSrc="/assets/promo/plus_widgets_tools.png"
						mediaAlt="Wishbox deposit and keep up widgets"
						dark
					>
						<Article>
							<Text lede>
								Deposit to a wishbox in a tap, or see when transactions went missing — from widgets
								on your home screen.
							</Text>
						</Article>
					</FeatureRow>
				</Section>
				<Section>
					<FeatureRow
						fit
						mediaMaxWidth={320}
						mediaSrc="/assets/mascot/Pose_SitThink.png"
						mediaAlt="Wishbox mascot"
					>
						<Article>
							<Text midExtreme>
								Meet the <Text color={colors.magenta}>mascot</Text>.
							</Text>
						</Article>
						<Article>
							<Text lede>
								It lives in <Text color={colors.purple}>3D</Text> on your widgets. Let it entertain
								you, or tap it to <Text color={colors.blue}>chat</Text> with the assistant. Part of{" "}
								<Text color={colors.purple}>Wishbox+</Text>.
							</Text>
						</Article>
					</FeatureRow>
				</Section>
				<Section>
					<FeatureRow
						invert
						phone
						imageUrl="/assets/screens/native-widgets.png"
						mediaAlt="Wishbox widgets on an iPhone home screen"
					>
						<Article>
							<Text lede>
								Home screen widgets are on <Text color={colors.blue}>iPhone</Text> now. The same
								widgets are coming on <Text color={colors.teal}>Android</Text>.
							</Text>
						</Article>
					</FeatureRow>
				</Section>
			</Band>

			<Band alt>
				<PlatformGrid />
			</Band>

			<Band>
				<Section>
					<FeatureRow
						invert
						fit
						mediaMaxWidth={360}
						mediaSrc="/assets/generated/privacy_local.png?v=3"
						mediaAlt="Private by default"
					>
						<Article>
							<Text midExtreme>
								<Text color={colors.purple}>Private</Text> until you{" "}
								<Text color={colors.blue}>choose</Text> otherwise.
							</Text>
						</Article>
						<Article>
							<Text lede>
								<Text color={colors.blue}>Wishbox</Text> is{" "}
								<Text color={colors.magenta}>local-first</Text>. Collaboration, iCloud or Google
								Drive, and the assistant only see what you turn on. Read the{" "}
								<Link href="/privacy">privacy policy</Link>.
							</Text>
						</Article>
					</FeatureRow>
				</Section>
			</Band>

			<Band plus id="plus">
				<PlusHighlights />
			</Band>

			<Band alt id="faq">
				<HomeFaq />
			</Band>

			<Band>
				<ClosingCta />
			</Band>
		</Layout>
	);
}
